import { Injectable } from '@angular/core';
import { FileSizePipe } from '@cs/common/pipes';
import { FormatRegistry } from './format-registry';
import { DateUtils } from '@cs/common/utils';
import { CsCultureProvider } from './culture-provider';
import {
	FormatProviders,
	FormatRegisteredItem,
	PropertyAnnotation,
	isNullOrUndefined,
} from '@cs/core';

declare const sffjs: any;

// Reused regexes (do not recreate per call)
const RX_NULL_AS_ZERO = /:nullAsZero/i;
const RX_PERCENTAGE = /{\d:[pP]\d}/;
const RX_FILESIZE = /:fileSize/i;
// Regex to *find* currency token for replacement
const RX_CURRENCY_TOKEN_MATCH = /(\{\d:[^}]*)[Cc](\d?[^}]*\})/;
// Regex to check if any currency token *exists* (after potential C->N replacement)
const RX_CURRENCY_PRESENT_ANY = /\{\d:(?:[^}]*)[Cc]\d?(?:[^}]*)\}/;
const RX_DATE_TOKENS =
							/\{\d:.*?(?:d{1,4}|M{1,4}|y{2,4}|H{1,2}|h{1,2}|m{1,2}|s{1,2}|t{1,2}).*?\}/;
const RX_IDDATE = /:iddate/i;
const RX_SIMPLE_PLACEHOLDER = /^{\d}$/; // Matches "{0}", "{1}", etc.

const isNil = (v: any): v is null | undefined => v == null;

// This object holds pre-analyzed format string details
interface FormatStringAnalysis {
	normalizedString: string;
	hasFileSize: boolean;
	hasPercent: boolean;
	hasIdDate: boolean;
	hasCurrencyTransform: boolean; // Did we transform C->N?
	hasDateTokens: boolean;
	isSimplePlaceholder: boolean; // e.g., "{0}"
	requiresSffjs: boolean; // A heuristic to decide if sffjs is needed at all
}

// Option: tune size based on distinct format strings in your app
const NORMALIZED_FMT_CACHE_SIZE = 256;

// Minimal LRU cache
class LruCache<K, V> {
	private map = new Map<K, V>();
	constructor(private capacity: number) {
		if (capacity <= 0) throw new Error('capacity must be > 0');
	}
	get(key: K): V | undefined {
		const v = this.map.get(key);
		if (v === undefined) return undefined;
		this.map.delete(key);
		this.map.set(key, v);
		return v;
	}
	set(key: K, value: V): void {
		if (this.map.has(key)) {
			this.map.delete(key);
		} else if (this.map.size >= this.capacity) {
			const lruKey = this.map.keys().next().value as K;
			this.map.delete(lruKey);
		}
		this.map.set(key, value);
	}
}

@Injectable()
export class FormatProviderService {
	private fileSizePipe = new FileSizePipe();
	private static cultureSet = false;

	// Cache for analyzed format strings
	private formatAnalysisCache = new LruCache<string, FormatStringAnalysis>(
		NORMALIZED_FMT_CACHE_SIZE
	);

	constructor(
		private formatRegistry: FormatRegistry,
		private csCulture: CsCultureProvider
	) {
		if (!FormatProviderService.cultureSet) {
			sffjs.setCulture(csCulture.getCulture());
			FormatProviderService.cultureSet = true;
		}
	}

	/**
		* Performs all initial string analysis and normalization once.
		* Caches results for performance.
		*/
	private analyzeAndNormalizeFormatString(
		originalFormatString: string
	): FormatStringAnalysis {
		let result = this.formatAnalysisCache.get(originalFormatString);
		if (result) {
			return result;
		}

		let currentFormatString = originalFormatString;
		let hasCurrencyTransform = false;

		// 1. Strip :nullAsZero marker first (it's a directive, not a formatting token for sffjs)
		if (currentFormatString.indexOf(':nullAsZero') !== -1) {
			currentFormatString = currentFormatString.replace(RX_NULL_AS_ZERO, '');
		}

		// 2. Normalize currency token if present (C/c -> N)
		if (RX_CURRENCY_PRESENT_ANY.test(currentFormatString)) {
			currentFormatString = currentFormatString.replace(
				RX_CURRENCY_TOKEN_MATCH,
				'$1N$2'
			);
			hasCurrencyTransform = true;
		}

		// 3. Perform all regex tests on the now-normalized string
		const hasFileSize = RX_FILESIZE.test(currentFormatString);
		const hasPercent = RX_PERCENTAGE.test(currentFormatString);
		const hasIdDate = RX_IDDATE.test(currentFormatString);
		const hasDateTokens = RX_DATE_TOKENS.test(currentFormatString);
		const isSimplePlaceholder = RX_SIMPLE_PLACEHOLDER.test(currentFormatString);

		// Heuristic: Does this format string actually require sffjs?
		// If it's just "{0}" and no other processing, we might skip sffjs.
		// However, sffjs might handle edge cases like null/undefined differently
		// or apply implicit toString(). For safety, we keep sffjs as default.
		// If `parse` can return a primitive and "{0}" is the format, `sffjs`
		// often just returns `String(value)`.
		const requiresSffjs = !(
			isSimplePlaceholder &&
			!hasFileSize &&
			!hasPercent &&
			!hasIdDate &&
			!hasDateTokens &&
			!hasCurrencyTransform
		);

		result = {
			normalizedString: currentFormatString,
			hasFileSize,
			hasPercent,
			hasIdDate,
			hasCurrencyTransform,
			hasDateTokens,
			isSimplePlaceholder,
			requiresSffjs,
		};
		this.formatAnalysisCache.set(originalFormatString, result);
		return result;
	}

	format(value: any, formatEntry: FormatRegisteredItem) {
		// 1) Trivial passthroughs
		if (value === '') return value;

		// Handle nullish value and nullAsZero option
		if ((isNil(value) || isNil(formatEntry)) && value !== '') {
			const fmt = formatEntry?.formatString;
			const useNullAsZero =
										isNil(value) &&
										(this.formatRegistry.options.nullAsZero ||
											(typeof fmt === 'string' && RX_NULL_AS_ZERO.test(fmt)));
			if (!useNullAsZero) return value;
			value = 0; // Short-circuit to zero
		} else if (isNil(formatEntry) || isNil(formatEntry?.formatString)) {
			return value;
		}

		let formatValue = value;
		const originalFormatString = formatEntry!.formatString!;

		// 2) Provider short-circuit
		if (formatEntry!.provider !== FormatProviders.DotNet) {
			// No other providers handled: passthrough
			return value;
		}

		// 3) Analyze and normalize format string ONCE, potentially from cache
		const analysis = this.analyzeAndNormalizeFormatString(
			originalFormatString
		);

		let currentFormatString = analysis.normalizedString;

		// 4) fileSize: transform and short-circuit immediately
		if (analysis.hasFileSize) {
			const fs = this.fileSizePipe.transform(value);
			return fs == null ? value : fs; // This bypasses sffjs entirely
		}

		// 5) Percentage: optional divide by 100
		if (analysis.hasPercent && this.formatRegistry.options.dividePercentageBy100) {
			if (typeof formatValue === 'number') {
				formatValue = formatValue / 100;
			}
		}

		// 6) ID date conversion
		if (analysis.hasIdDate) {
			const d = DateUtils.convertCfDateToJsDate(formatValue);
			if (d == null) return value;
			formatValue = d;
			// Remove :iddate from the string as it's a value conversion marker,
			// not an sffjs format token. Do this *after* conversion.
			currentFormatString = currentFormatString.replace(RX_IDDATE, '');
		}

		// 7) Date coercion only if date tokens exist AND value is not already a Date
		if (analysis.hasDateTokens) {
			if (!(formatValue instanceof Date)) {
				if (DateUtils.isCohelionDateFormat(formatValue)) {
					const d = DateUtils.convertCfDateToJsDate(formatValue);
					if (d == null) return value;
					formatValue = d;
				} else {
					const t = typeof formatValue;
					if (t === 'string' || t === 'number') {
						const d = new Date(formatValue as any);
						if (isNaN(d.getTime())) return value; // Invalid date, return original
						formatValue = d;
					} else {
						return value; // Cannot convert non-string/non-number to Date
					}
				}
			}
		}

		// 8) Final render; limit try scope
		// If analysis indicates sffjs might not be strictly needed (e.g., "{0}"),
		// and `parse` simply returns the value, we could skip it.
		// However, for robust compatibility with sffjs, it's safer to call it
		// unless you are 100% sure of behavior equivalence.
		try {
			return sffjs(currentFormatString, formatEntry!.parse(formatValue));
		} catch {
			return value; // Safe fallback
		}
	}

	// ... (formatByDataType, formatByPropertyAnnotation, formatByString remain unchanged)

	getFormatEntry(type: string) {
		return this.formatRegistry.getFormatEntry(type);
	}

	formatByDataType(value: any, type: string) {
		if (isNil(value)) return value;
		const formatEntry = this.formatRegistry.getFormatEntry(type);
		return this.format(value, formatEntry);
	}

	formatByPropertyAnnotation(
		optionList: PropertyAnnotation<unknown>,
		value: any
	) {
		return this.format(value, new FormatRegisteredItem(null, optionList.format));
	}

	formatByString(value: any = null, formatString: string = null): string {
		return this.format(value, new FormatRegisteredItem(null, formatString));
	}
}
